import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Container,
  InputAdornment,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  getFacilitiesPaginated,
  createFacility,
  updateFacility,
  getStates,
  CreateFacilityPayload,
  FacilityRecord,
  StateRecord,
} from '../services/claimsApi';
import PageControls from '../components/PageControls';

const emptyForm: CreateFacilityPayload = {
  location_code: '',
  name: '',
  address: '',
  city: '',
  state: '',
  postal_code: '',
  phone: '',
  email: '',
  active: true,
};

const facilityToForm = (facility: FacilityRecord): CreateFacilityPayload => ({
  location_code: facility.location_code ?? '',
  name: facility.name ?? '',
  address: facility.address ?? '',
  city: facility.city ?? '',
  state: facility.state ?? '',
  postal_code: facility.postal_code ?? '',
  phone: facility.phone ?? '',
  email: facility.email ?? '',
  active: Boolean(facility.active),
});

const columnToSortField: Record<string, string> = {
  'Location Code': 'location_code',
  Status: 'active',
};

type StatusFilter = 'all' | 'enabled' | 'disabled';

const statusFilterToActive = (filter: StatusFilter): boolean | undefined => {
  if (filter === 'all') return undefined;
  return filter === 'enabled';
};

const Facilities: React.FC = () => {
  const [facilities, setFacilities] = useState<FacilityRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState<FacilityRecord | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CreateFacilityPayload>(emptyForm);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('location_code');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [submittedSearchQuery, setSubmittedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('enabled');
  const [states, setStates] = useState<StateRecord[]>([]);
  const [statesLoading, setStatesLoading] = useState(true);

  const isEditMode = editingFacility !== null;

  const loadFacilities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getFacilitiesPaginated({
        page,
        pageSize,
        sortBy,
        sortOrder,
        search: submittedSearchQuery || undefined,
        active: statusFilterToActive(statusFilter),
      });
      setFacilities(response.data);
      setTotal(response.total);
    } catch {
      setError('Failed to load facilities. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortOrder, submittedSearchQuery, statusFilter]);

  useEffect(() => {
    loadFacilities();
  }, [loadFacilities]);

  useEffect(() => {
    const loadStates = async () => {
      setStatesLoading(true);
      try {
        const data = await getStates();
        setStates(Array.isArray(data) ? data : []);
      } catch {
        setStates([]);
      } finally {
        setStatesLoading(false);
      }
    };
    loadStates();
  }, []);

  const handleOpenAdd = () => {
    setEditingFacility(null);
    setForm(emptyForm);
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleOpenEdit = (facility: FacilityRecord) => {
    setEditingFacility(facility);
    setForm(facilityToForm(facility));
    setFormErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    if (!saving) {
      setDialogOpen(false);
      setEditingFacility(null);
    }
  };

  const handleFormChange = (field: keyof CreateFacilityPayload, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const parseApiErrors = (err: unknown): Record<string, string> => {
    if (typeof err === 'object' && err !== null && 'response' in err) {
      const data = (err as { response?: { data?: { errors?: Record<string, string[]> } } }).response?.data;
      if (data?.errors) {
        return Object.fromEntries(
          Object.entries(data.errors).map(([key, messages]) => [key, messages[0] ?? 'Invalid value'])
        );
      }
    }
    return {};
  };

  const validateFacilityForm = (payload: CreateFacilityPayload): Record<string, string> => {
    const errors: Record<string, string> = {};

    const requiredFields: Array<keyof CreateFacilityPayload> = [
      'location_code',
      'name',
      'address',
      'city',
      'postal_code',
    ];

    requiredFields.forEach((field) => {
      const value = String(payload[field] ?? '').trim();
      if (!value) {
        errors[field] = 'This field is required';
      }
    });

    const stateCode = String(payload.state ?? '').trim();
    if (!stateCode) {
      errors.state = 'This field is required';
    } else if (states.length > 0 && !states.some((s) => s.code === stateCode)) {
      errors.state = 'Select a valid state';
    }

    const postalCode = String(payload.postal_code ?? '').trim();
    if (postalCode && !/^\d{5}(-\d{4})?$/.test(postalCode)) {
      errors.postal_code = 'Enter a valid postal code (12345 or 12345-6789)';
    }

    const phone = String(payload.phone ?? '').trim();
    const phoneDigits = phone.replace(/\D/g, '');
    if (phone && phoneDigits.length !== 10) {
      errors.phone = 'Enter a valid 10-digit phone number';
    }

    const email = String(payload.email ?? '').trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Enter a valid email address';
    }

    return errors;
  };

  const handleSort = (column: string) => {
    const field = columnToSortField[column];
    if (!field) return;

    if (sortBy === field) {
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newOrder);
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
    setPage(1);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedSearchQuery(searchQuery.trim());
    setPage(1);
  };

  const handleStatusFilterChange = (event: SelectChangeEvent<StatusFilter>) => {
    setStatusFilter(event.target.value as StatusFilter);
    setPage(1);
  };

  const handleReset = () => {
    setSearchQuery('');
    setSubmittedSearchQuery('');
    setStatusFilter('enabled');
    setPage(1);
    setSortBy('location_code');
    setSortOrder('asc');
    setPageSize(100);
  };

  const handleSave = async () => {
    const normalizedForm: CreateFacilityPayload = {
      location_code: String(form.location_code ?? '').trim(),
      name: String(form.name ?? '').trim(),
      address: String(form.address ?? '').trim(),
      city: String(form.city ?? '').trim(),
      state: String(form.state ?? '').trim(),
      postal_code: String(form.postal_code ?? '').trim(),
      phone: String(form.phone ?? '').trim() || undefined,
      email: String(form.email ?? '').trim() || undefined,
      active: Boolean(form.active),
    };

    const clientErrors = validateFacilityForm(normalizedForm);
    if (Object.keys(clientErrors).length > 0) {
      setFormErrors(clientErrors);
      return;
    }

    setSaving(true);
    setFormErrors({});
    setError(null);
    try {
      if (isEditMode && editingFacility) {
        await updateFacility(editingFacility.id, normalizedForm);
        setSuccess(`${editingFacility.location_code} updated successfully.`);
      } else {
        await createFacility(normalizedForm);
        setSuccess('Facility added successfully.');
      }
      setDialogOpen(false);
      setEditingFacility(null);
      await loadFacilities();
    } catch (err) {
      const apiErrors = parseApiErrors(err);
      if (Object.keys(apiErrors).length > 0) {
        setFormErrors(apiErrors);
      } else {
        setError(
          isEditMode ? 'Failed to update facility. Please try again.' : 'Failed to add facility. Please try again.'
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const renderSortableHeader = (label: string) => {
    const field = columnToSortField[label];
    const isActive = field && sortBy === field;
    return (
      <TableCell onClick={() => handleSort(label)} sx={{ cursor: 'pointer', userSelect: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {label}
          {isActive &&
            (sortOrder === 'asc' ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />)}
        </Box>
      </TableCell>
    );
  };

  if (error && facilities.length === 0 && !loading) {
    return (
      <Container>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ width: '100%', px: 2 }}>
      <Box sx={{ my: 4 }}>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}
        {error && facilities.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="h4" component="h1">
            Facilities
          </Typography>

          <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} sx={{ textTransform: 'none' }}>
            Add Facility
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <PageControls
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              itemLabel="Facilities"
            />

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="facility-status-filter-label">Status</InputLabel>
              <Select
                labelId="facility-status-filter-label"
                id="facility-status-filter"
                value={statusFilter}
                label="Status"
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="enabled">Enabled</MenuItem>
                <MenuItem value="disabled">Disabled</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search facilities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button type="submit" size="small">
                        <SearchIcon />
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Typography
              component="a"
              onClick={(e) => {
                e.preventDefault();
                handleReset();
              }}
              sx={{
                fontSize: '0.875rem',
                color: 'primary.main',
                textDecoration: 'underline',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                '&:hover': { color: 'primary.dark' },
              }}
            >
              Reset
            </Typography>
          </Box>
        </Box>

        <Box sx={{ position: 'relative' }}>
          <TableContainer component={Paper} sx={{ width: '100%' }}>
            <Table sx={{ width: '100%' }}>
              <TableHead>
                <TableRow>
                  {renderSortableHeader('Location Code')}
                  <TableCell>Name</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>State</TableCell>
                  {renderSortableHeader('Status')}
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {facilities.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No facilities found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  facilities.map((facility) => (
                    <TableRow key={facility.id}>
                      <TableCell>{facility.location_code || 'N/A'}</TableCell>
                      <TableCell>{facility.name || 'N/A'}</TableCell>
                      <TableCell>{facility.city || 'N/A'}</TableCell>
                      <TableCell>{facility.state || 'N/A'}</TableCell>
                      <TableCell>
                        <Chip
                          label={facility.active ? 'Enabled' : 'Disabled'}
                          color={facility.active ? 'success' : 'default'}
                          size="small"
                          sx={
                            !facility.active
                              ? {
                                  backgroundColor: '#fce4ec',
                                  color: '#c62828',
                                  '& .MuiChip-label': { color: '#c62828' },
                                }
                              : undefined
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleOpenEdit(facility)}
                          sx={{ fontSize: '1.1rem', padding: '6px 16px', textTransform: 'none' }}
                        >
                          Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {loading && facilities.length === 0 && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(15, 18, 25, 0.72)',
                zIndex: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          {loading && facilities.length > 0 && (
            <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
              <CircularProgress size={24} />
            </Box>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <PageControls
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            itemLabel="Facilities"
          />
        </Box>
      </Box>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>{isEditMode ? 'Edit Facility' : 'Add Facility'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Location Code"
              required
              fullWidth
              size="small"
              value={form.location_code}
              onChange={(e) => handleFormChange('location_code', e.target.value)}
              error={!!formErrors.location_code}
              helperText={formErrors.location_code}
            />
            <TextField
              label="Name"
              required
              fullWidth
              size="small"
              value={form.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
            <TextField
              label="Address"
              required
              fullWidth
              size="small"
              value={form.address}
              onChange={(e) => handleFormChange('address', e.target.value)}
              error={!!formErrors.address}
              helperText={formErrors.address}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="City"
                required
                fullWidth
                size="small"
                value={form.city}
                onChange={(e) => handleFormChange('city', e.target.value)}
                error={!!formErrors.city}
                helperText={formErrors.city}
              />
              <FormControl fullWidth size="small" required error={!!formErrors.state}>
                <InputLabel id="facility-state-label">State</InputLabel>
                <Select
                  labelId="facility-state-label"
                  label="State"
                  value={form.state ?? ''}
                  onChange={(e) => handleFormChange('state', e.target.value)}
                  disabled={statesLoading}
                >
                  {states.map((state) => (
                    <MenuItem key={state.id} value={state.code}>
                      {state.name}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.state && <FormHelperText>{formErrors.state}</FormHelperText>}
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Postal Code"
                required
                fullWidth
                size="small"
                value={form.postal_code}
                onChange={(e) => handleFormChange('postal_code', e.target.value)}
                error={!!formErrors.postal_code}
                helperText={formErrors.postal_code}
              />
              <TextField
                label="Phone"
                fullWidth
                size="small"
                value={form.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
              />
            </Box>
            <TextField
              label="Email"
              fullWidth
              size="small"
              type="email"
              value={form.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.active ?? true}
                  onChange={(e) => handleFormChange('active', e.target.checked)}
                />
              }
              label={form.active ? 'Enabled — new claims allowed' : 'Disabled — new claims blocked'}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !form.location_code.trim()}
            sx={{ textTransform: 'none' }}
          >
            {saving ? 'Saving...' : isEditMode ? 'Save Changes' : 'Add Facility'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Facilities;
